import { IMaintenanceVisualRecord } from "../../types/maintenance-visual-record.type";
import {
  budgetItem,
  IContestation,
  IMaintenance,
  IMaintenanceHistoryItem,
  ProductOrServiceType,
} from "../../types/maintenance.type";
import { IVehicle } from "../../types/vehicle.type";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";
import { getCompanyId } from "./company.api";

const API_URL = import.meta.env.VITE_API_URL;

const makeRequestDebounced = createDebounce(makeRequest, 200);

type GetMaintenanceParams = {
  status?: string[] | string;
  search?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  itemsPerPage?: string;
  orderBy?: string;
  partnerId?: string | string[];
  auditStatus?: string;
  fields?: string;
};

type Photo = {
  type: string;
  content: string;
};

interface AddPhotoMaintenanceVisualRecordData {
  docType: string;
  visualRecordId: string;
  photos: Photo[];
}

export async function getMaintenance({
  status = [],
  search = "",
  startDate = "",
  endDate = "",
  page = 100,
  itemsPerPage = "",
  orderBy = "-updatedAt",
  partnerId,
  auditStatus,
  fields = "name, licensePlate, whatsapp, status, updatedAt, createdAt, vehicleId,renterId, waitingTicket, mechanic, typePlanRenter, typeMaintenance,companyId,chargeMaintenance,collision,diagnosis",
}: GetMaintenanceParams = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });

  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  if (partnerId) {
    if (Array.isArray(partnerId) && partnerId.length > 0) {
      params.append("filters[partnerId]", partnerId.join(","));
    } else if (typeof partnerId === "string" && partnerId) {
      params.append("filters[partnerId]", partnerId);
    }
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  itemsPerPage && params.append("itemsPerPage", itemsPerPage);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());
  let auditStatusParam = "";
  if (Array.isArray(auditStatus) && auditStatus.length > 0) {
    auditStatusParam = auditStatus.join(","); // Ex: "true,false"
  } else if (typeof auditStatus === "string") {
    auditStatusParam = auditStatus;
  }
  auditStatusParam &&
    params.append("filters[chargeMaintenance.confirmed]", auditStatusParam);
  // orderBy && params.append("orderBy", sortConfig ? `-${sortConfig}` : "-updatedAt");

  const url = `${API_URL}/maintenance?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IMaintenance[]
  > & { totalItems: number };

  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}
export async function getMaintenanceAudit({
  status = [],
  search = "",
  startDate = "",
  endDate = "",
  page = 100,
  itemsPerPage = "",
  orderBy = "-updatedAt",
  partnerId,
  auditStatus,
  fields = "name, licensePlate, whatsapp, status, updatedAt, createdAt, vehicleId,renterId, waitingTicket, mechanic, typePlanRenter, typeMaintenance,companyId,chargeMaintenance,diagnosis",
}: GetMaintenanceParams = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });

  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  if (partnerId) {
    if (Array.isArray(partnerId) && partnerId.length > 0) {
      params.append("filters[partnerId]", partnerId.join(","));
    } else if (typeof partnerId === "string" && partnerId) {
      params.append("filters[partnerId]", partnerId);
    }
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  itemsPerPage && params.append("itemsPerPage", itemsPerPage);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());

  if (typeof auditStatus === "string" && auditStatus) {
    params.append("filters[auditStatus]", auditStatus);
  } else if (Array.isArray(auditStatus) && auditStatus.length > 0) {
    params.append("filters[auditStatus]", auditStatus.join(","));
  }

  const url = `${API_URL}/maintenance-audit?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IMaintenance[]
  > & { totalItems: number };

  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}

export async function getMaintenanceDrag({
  startDate = "",
  endDate = "",
} = {}) {
  const params = new URLSearchParams({});
  startDate && params.append("startDate", startDate.toString());
  endDate && params.append("endDate", endDate.toString());

  const url = `${API_URL}/maintenance-dashboard?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IMaintenance[]
  >;

  return { ok: result.ok, data: result.data };
}

export async function getMaintenanceReports({
  status = [],
  search = "",
  startDate = "",
  endDate = "",
  startDateAtualizadoEm = "",
  endDateAtualizadoEm = "",
  page = 1,
  itemsPerPage = "",
  orderBy = "-createdAt",
  fields = "name, licensePlate, whatsapp, status, odometer, createdAt, vehicleId, renterId, waitingTicket, partnerCompanyName, typePlanRenter, chargeMaintenance, companyId",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: itemsPerPage,
    fields,
  });
  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  itemsPerPage && params.append("itemsPerPage", itemsPerPage);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());
  startDateAtualizadoEm &&
    params.append(
      "filters[startDateAtualizadoEm]",
      startDateAtualizadoEm.toString()
    );
  endDateAtualizadoEm &&
    params.append(
      "filters[endDateAtualizadoEm]",
      endDateAtualizadoEm.toString()
    );

  const url = `${API_URL}/reports/maintenance?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IMaintenance[]
  > & { totalItems: number };

  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}

export async function getTicketStock(search: string = "") {
  const params = new URLSearchParams({});
  search && params.append("search", search);

  const url = `${API_URL}/ticket-pieces?${params.toString()}`;

  return makeRequestDebounced(url) as Promise<
    ApiResponseType<
      {
        name: string;
        code: string;
        quantity: number;
        licensePlate: string;
        mechanic: string;
        waitingTicket: number;
        isRemovedFromStock: boolean;
      }[]
    >
  >;
}

export async function getMaintenanceByLicensePlate(licensePlate: string) {
  const url = `${API_URL}/reports-maintenance-licensePlate/${licensePlate}`;

  return makeRequestDebounced(url) as Promise<ApiResponseType<IMaintenance[]>>;
}

export async function getMaintenanceById(maintenanceId: string) {
  const url = `${API_URL}/maintenance/${maintenanceId}`;

  return makeRequestDebounced(url) as Promise<ApiResponseType<IMaintenance>>;
}

export async function createMaintenance(maintenanceData) {
  const data = {
    ...maintenanceData,
    companyId: getCompanyId(),
  };
  const url = `${API_URL}/maintenance`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequest(url, options) as Promise<
    ApiResponseType<
      | IMaintenance
      | { vehicleAnotherCompany: boolean; vehicle: IVehicle; city: string }
    >
  >;
}

export async function verifyVehicleMaintenance(licensePlate: string) {
  const url = `${API_URL}/maintenance-verify-vehicle/${licensePlate}`;
  const options = {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  };
  return makeRequest(url, options) as Promise<
    ApiResponseType<{
      vehicleAnotherCompany: boolean;
      vehicle: IVehicle;
      city: string;
    }>
  >;
}

const removeReadonlyFields = (updatedData) => {
  delete updatedData._id;
  delete updatedData.__v;
  delete updatedData.contacts;
  delete updatedData.pendencies;
  delete updatedData.status;
  delete updatedData.updatedAt;
  delete updatedData.createdAt;
};

export async function updateMaintenance<T>(id: string, updatedData: T) {
  removeReadonlyFields(updatedData);
  const data = {
    ...updatedData,
    companyId: getCompanyId(),
  };
  const url = `${API_URL}/maintenance/${id}`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  const result = makeRequest(url, options);

  return result;
}

const makeRequestDebouncedMaintenance = createDebounce(makeRequest, 250);
export async function getImageMaintenanceVisualRecord(searchId: string) {
  const url = `${API_URL}/maintenance/visual-records/${searchId}`;
  const result = (await makeRequestDebouncedMaintenance(
    url
  )) as ApiResponseType<{
    maintenancesVisualRecords: IMaintenanceVisualRecord[];
    maintenances: IMaintenance[];
  }>;

  return result;
}

export async function getDataRenterVehicle(maintenanceId: string) {
  const url = `${API_URL}/maintenance/${maintenanceId}/edit`;
  const result = await makeRequest<IMaintenance>(url);

  // Type guard to check if result is of type ApiResponseType<IMaintenance>
  function isApiResponseType<T>(result: unknown): result is ApiResponseType<T> {
    return (result as ApiResponseType<T>).data !== undefined;
  }

  // Check if result has the data property
  if (isApiResponseType<IMaintenance>(result)) {
    if (Array.isArray(result.data)) {
      Object.assign(result, { data: result.data });
    }
    return result;
  } else {
    // Handle the case where result is not an ApiResponseType
    return {
      ok: false,
      data: {} as IMaintenance,
      errors: "Unexpected response format",
      message: "Unexpected response format",
    };
  }
}

type BudgetMaintenanceData = {
  _id: string;
  budgetMaintenance: ProductOrServiceType;
  observation: Observation;
};

export async function updateBudgetMaintenance(data: BudgetMaintenanceData) {
  const url = `${API_URL}/maintenance/budget`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequest(url, options);
}

export async function getWorkAndProductsParam(search: string) {
  const url = `${API_URL}/maintenance-workAndProducts/${search}`;
  const options = {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  };
  return makeRequest<budgetItem[]>(url, options);
}

export async function getWorkAndProducts() {
  const url = `${API_URL}/maintenance-workAndProducts`;
  const options = {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  };
  return makeRequest<budgetItem[]>(url, options);
}

export async function changeStatusMaintenance(data) {
  const url = `${API_URL}/maintenance/${data.maintenanceId}/status`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequest(url, options);
}

const removeFields = (updatedData) => {
  delete updatedData._id;
  delete updatedData.__v;
  delete updatedData.name;
  delete updatedData.renterId;
  delete updatedData.vehicleId;
  delete updatedData.color;
  delete updatedData.chassi;
  delete updatedData.whatsapp;
  delete updatedData.registers;
  delete updatedData.whatsapp;
};

export async function editWaitingTicketAndMechanic(data) {
  removeFields(data);
  const url = `${API_URL}/maintenance/${data.maintenanceId}/update-ticket-or-mechanic`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IMaintenance>
  >;
  return result;
}

// foi utlizado apenas para o pdf do felipe
interface MaintenanceItem {
  sequencial_number: number;
  pieceName: string;
  quantityAndPrice: {
    quantity: number;
    sellPrice: number;
  };
  typeOfPiece: string;
}

interface Observation {
  author: string;
  text: string;
}

// pdf do felipe
export interface MaintenanceTemplateData {
  typeOfReview: string;
  dataTime: Date;
  name: string;
  whatsapp: string;
  model: string;
  licensePlate: string;
  chassi: string;
  typeOfEvent: string;
  color: string;
  maintenanceItems: MaintenanceItem[];
  waitingTicket: number;
  mechanic: string;
  totalPreventivePrice: string;
  totalCorrectivePrice: string;
  totalMisusePrice: string;
  observations: Observation[];
  typePlanRenter: string;
  cobrancaTotal?: number;
}

export async function getBudgetMaintenancePDF(id: string) {
  const url = `${API_URL}/maintenance/${id}/pdf`;
  const options = {
    method: "POST",
  };
  return makeRequest<{
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    chassi(chassi: any): unknown;
    MaintenanceTemplateData;
  }>(url, options);
}

export async function asaasMaintenance(data) {
  const url = `${API_URL}/maintenance/${data._id}/asaas`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequest(url, options);
}

type SaveVehicleRecordsProps = {
  renterId: string;
  vehicleId: string;
  photos?: { content: string }[];
  photosMaintenance?: { content: string }[];
  nameRenter?: string;
  complement?: string;
};

export async function maintenanceStartVisualRecords(
  data: SaveVehicleRecordsProps
) {
  const url = `${API_URL}/maintenance/visual-records`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<SaveVehicleRecordsProps>
  >;
}

const makeRequestDebouncedUpdateForecastMaintenance = createDebounce(
  makeRequest,
  200
);
export async function updateForecastMaintenance(data: {
  forecastMaintenance: { dateNextMaintenance: Date; odometer: number };
  maintenanceId: string;
}) {
  const url = `${API_URL}/maintenance/forecast-maintenance`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebouncedUpdateForecastMaintenance(url, options) as Promise<
    ApiResponseType<{
      dateNextMaintenance: Date;
      odometer: number;
      maintenanceId: string;
    }>
  >;
}

export async function addPhotoMaintenanceVisualRecord(
  data: AddPhotoMaintenanceVisualRecordData
): Promise<ApiResponseType<IMaintenanceVisualRecord>> {
  const url = `${API_URL}/maintenance/visual-records/addFile`;

  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  const result = (await makeRequestDebounced(
    url,
    options
  )) as ApiResponseType<IMaintenanceVisualRecord>;
  return result;
}

export async function addMaintenanceObservation(
  visualRecordId: string,
  newObservationText: string
): Promise<ApiResponseType<IMaintenanceVisualRecord>> {
  const url = `${API_URL}/maintenance/visual-records/${visualRecordId}/observation`;

  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text: newObservationText }),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IMaintenanceVisualRecord>
  >;
  return result;
}

export async function updateMaintenanceEndVisualRecords(
  data: SaveVehicleRecordsProps
) {
  const url = `${API_URL}/maintenance/visual-records/end-maintenance`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<SaveVehicleRecordsProps>
  >;
}
export async function getMaintenanceHistory(licensePlate: string) {
  const url = `${API_URL}/maintenance/history/${licensePlate}`;

  const result = (await makeRequest(url)) as ApiResponseType<
    IMaintenanceHistoryItem[]
  > & { totalItems: number };

  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}
export async function contestMaintenanceCharge(
  maintenanceId: string,
  contestationData: IContestation
): Promise<ApiResponseType<{ ok: boolean; message: string }>> {
  const url = `${API_URL}/maintenance/${maintenanceId}/contest`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(contestationData),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<{ ok: boolean; message: string }>
  >;
}

export async function confirmMaintenanceCharge(maintenanceId: string) {
  const url = `${API_URL}/maintenance/${maintenanceId}/confirm-charge`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  };
  return makeRequest(url, options);
}

export async function checkConfirmkMaintenance(maintenanceId: string, data) {
  const url = `${API_URL}/maintenance/${maintenanceId}/check-confirm`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IMaintenance>
  >;
}

export type TypeBudgetGurmet = {
  maintenanceId: string;
  _id: string;
  code: string;
  name: string;
  quantity: number;
  licensePlate: string;
  mechanic: string;
  isRemovedFromStock: boolean;
  actionStatus: "corrective" | "preventive" | "misuse";
};

export async function budgetRefreshMaintenance(data: TypeBudgetGurmet) {
  const url = `${API_URL}/maintenances/budgetRefresh`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<unknown>
  >;
}

export type MaintenanceHistoryTypeUpdate = {
  historyItemId: string;
  newType: "corrective" | "preventive" | "misuse";
};

export async function updateMaintenanceHistoryTypes(
  maintenanceId: string,
  updates: MaintenanceHistoryTypeUpdate[]
) {
  const url = `${API_URL}/maintenance/${maintenanceId}/update-history-types`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ updates }),
  };
  return makeRequest(url, options);
}
